<script lang="ts" setup>
import type { ToastMessageOptions } from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { Config_Pull_Stage, Config_Pull_Status } from "~/types/graphql";
import { useI18n } from 'vue-i18n';

const toast = useToast();
const { queue } = storeToRefs(useClientsStore());
const { t } = useI18n();
const displayedProgress = reactive(new Map<number, number>());
const previousTargetProgress = reactive(new Map<number, number>());
const progressHoldUntil = reactive(new Map<number, number>());
const waitingDotsFrame = ref(0);

let waitingDotsTick = 0;

let progressAnimationTicker: ReturnType<typeof setInterval> | undefined;

const stageLabelMap: Record<Config_Pull_Stage, string> = {
  [Config_Pull_Stage.ReceivingFile]: 'config.toast.pull.stages.receivingFile',
  [Config_Pull_Stage.Validating]: 'config.toast.pull.stages.validating',
  [Config_Pull_Stage.Parsing]: 'config.toast.pull.stages.parsing',
  [Config_Pull_Stage.Extracting]: 'config.toast.pull.stages.extracting',
  [Config_Pull_Stage.Finalizing]: 'config.toast.pull.stages.finalizing',
  [Config_Pull_Stage.UpdatingHash]: 'config.toast.pull.stages.updatingHash',
  [Config_Pull_Stage.Completed]: 'config.toast.pull.stages.completed',
};

function isTerminalError(status: Config_Pull_Status | 'PENDING' | 'ERROR') {
  return status === 'ERROR' || status === Config_Pull_Status.NotConnected || status === Config_Pull_Status.Timeout;
}

function isCompletedSuccess(entry: { status: Config_Pull_Status | 'PENDING' | 'ERROR' }) {
  return entry.status === Config_Pull_Status.Success;
}

function isSuccessReadyToClose(key: number) {
  return (displayedProgress.get(key) ?? 0) >= 100;
}

function finalizeSuccess(key: number, entry: ReturnType<typeof getQueueEntry>) {
  if (!entry) return;
  displayedProgress.set(key, 100);
  previousTargetProgress.delete(key);
  progressHoldUntil.delete(key);
  if (entry.message) {
    toast.remove(entry.message);
  }
  toast.add({
    severity: "success",
    group: "headless",
    summary: t('config.toast.loaded'),
    life: 3000,
  });
  entry.message = undefined;
  entry.subscription?.unsubscribe();
  queue.value.delete(key);
}

function getQueueEntry(key?: number) {
  if (key === undefined) return undefined;
  return queue.value.get(key) as {
    status: Config_Pull_Status | 'PENDING' | 'ERROR';
    progress: number;
    stage?: Config_Pull_Stage;
    statusMessage?: string;
    stepKey?: string;
    subscription: any;
    message?: ToastMessageOptions;
  } | undefined;
}

function getProgressLabel(key?: number) {
  const progress = getProgressPercent(key);
  return t('config.toast.pull.progressLabel', { progress });
}

function getStageLabel(key?: number) {
  const entry = getQueueEntry(key);
  if (!entry) return '';
  const isPending = entry.status === 'PENDING' && getProgressPercent(key) < 100;

  const withWaitingSuffix = (label: string, isDoneLabel: boolean) => {
    if (!isPending || isDoneLabel) return label;
    const dots = '.'.repeat(waitingDotsFrame.value);
    return dots ? `${label} ${dots}` : label;
  };

  if (entry.statusMessage) {
    const isDoneLabel = entry.statusMessage === 'config.toast.pull.steps.done';
    if (
      isDoneLabel &&
      getProgressPercent(key) < 100
    ) {
      return t('config.toast.pull.steps.finalizing');
    }
    return withWaitingSuffix(t(entry.statusMessage), isDoneLabel);
  }
  if (entry.stepKey) {
    const isDoneLabel = entry.stepKey === 'config.toast.pull.steps.done';
    if (
      isDoneLabel &&
      getProgressPercent(key) < 100
    ) {
      return t('config.toast.pull.steps.finalizing');
    }
    return withWaitingSuffix(t(entry.stepKey), isDoneLabel);
  }
  if (entry.stage) {
    return withWaitingSuffix(
      t(stageLabelMap[entry.stage]),
      entry.stage === Config_Pull_Stage.Completed,
    );
  }
  return withWaitingSuffix(t('config.toast.pull.stages.unknown'), false);
}

function getProgressPercent(key?: number) {
  const entry = getQueueEntry(key);
  if (!entry) return 0;
  const smoothed = displayedProgress.get(key!);
  if (typeof smoothed === 'number') return Math.round(smoothed);
  return Math.min(100, Math.max(0, Math.round(entry.progress ?? 0)));
}

function getTargetProgress(entry: { status: Config_Pull_Status | 'PENDING' | 'ERROR'; progress: number }) {
  if (entry.status === Config_Pull_Status.Success) return 100;
  return Math.min(100, Math.max(0, entry.progress ?? 0));
}

function tickProgressAnimation() {
  waitingDotsTick += 1;
  if (waitingDotsTick % 10 === 0) {
    waitingDotsFrame.value = (waitingDotsFrame.value + 1) % 4;
  }

  queue.value.forEach((entry, key) => {
    const target = getTargetProgress(entry);
    const previousTarget = previousTargetProgress.get(key);
    if (previousTarget === undefined) {
      previousTargetProgress.set(key, target);
    } else if (previousTarget !== target) {
      previousTargetProgress.set(key, target);
      // Small pause between backend jumps to feel less mechanical.
      progressHoldUntil.set(key, Date.now() + 70);
    }

    const holdUntil = progressHoldUntil.get(key) ?? 0;
    if (Date.now() < holdUntil) {
      return;
    }

    const current = displayedProgress.get(key) ?? Math.min(2, target);

    if (target <= current) {
      displayedProgress.set(key, target);
      return;
    }

    // Ease towards each backend update to avoid visible percentage jumps.
    const delta = target - current;
    const baseStep = Math.max(0.45, Math.min(3.2, delta * 0.28));
    // Slightly faster finish when backend already reported SUCCESS.
    const step = entry.status === Config_Pull_Status.Success
      ? Math.max(1.3, baseStep)
      : baseStep;
    displayedProgress.set(key, Math.min(target, current + step));

    if (entry.status === Config_Pull_Status.Success && isSuccessReadyToClose(key)) {
      finalizeSuccess(key, getQueueEntry(key));
    }
  });

  for (const key of displayedProgress.keys()) {
    if (!queue.value.has(key)) {
      displayedProgress.delete(key);
      previousTargetProgress.delete(key);
      progressHoldUntil.delete(key);
    }
  }
}

onMounted(() => {
  progressAnimationTicker = setInterval(tickProgressAnimation, 80);
});

onBeforeUnmount(() => {
  if (progressAnimationTicker) {
    clearInterval(progressAnimationTicker);
    progressAnimationTicker = undefined;
  }
});

// TODO: message composable
watch(
  queue,
  (newValue) => {
    newValue.forEach((value, key) => {
      if (isTerminalError(value.status)) {
        displayedProgress.delete(key);
        previousTargetProgress.delete(key);
        progressHoldUntil.delete(key);
        if (value.message) {
          toast.remove(value.message);
        }
        toast.add({
          severity: "error",
          group: "headless",
          summary: t('config.toast.loadError'),
          life: 3000,
        });
        value.message = undefined;
        value.subscription?.unsubscribe();
        newValue.delete(key);
        return;
      }

      if (isCompletedSuccess(value)) {
        if (!isSuccessReadyToClose(key)) {
          return;
        }
        finalizeSuccess(key, getQueueEntry(key));
        return;
      }

      if (value.status === "PENDING") {
        if (!displayedProgress.has(key)) {
          displayedProgress.set(key, Math.min(2, getTargetProgress(value)));
          previousTargetProgress.set(key, getTargetProgress(value));
          progressHoldUntil.delete(key);
        }
        if (value.message) return;
        const message = {
          severity: "custom",
          summary: t('config.toast.pull.title'),
          group: "headless",
          life: 0,
          detail: {
            key,
          },
        };
        value.message = message;
        toast.add(message);
      }
    });
  },
  { deep: true }
);

function clearMessage({ message }: { message: ToastMessageOptions }) {
  const key = message.detail?.key;
  if (key !== undefined) {
    displayedProgress.delete(key);
    previousTargetProgress.delete(key);
    progressHoldUntil.delete(key);
    const subscription = queue.value.get(key);
    if (subscription) {
      subscription?.subscription.unsubscribe();
    }
    queue.value.delete(key);
  }
}
</script>
<template>
  <Toast position="bottom-right" group="headless" @close="clearMessage">
    <template #container="{ message, closeCallback }">
      <!-- Custom progress toast -->
      <section
        v-if="message.severity === 'custom'"
        class="flex p-3 gap-3 w-full border-round toast-pull-container"
      >
        <div class="flex gap-3 w-full align-items-center">
          <i class="pi pi-cloud-upload text-2xl"/>
          <div class="flex flex-column gap-2 w-full">
            <div class="flex align-items-center gap-2 w-full">
              <p class="m-0 font-semibold text-base">
                {{ message.summary }}
              </p>
              <span class="ml-auto text-sm font-medium">
                {{ getProgressLabel(message?.detail?.key) }}
              </span>
            </div>
            <p class="m-0 text-sm">
              {{ getStageLabel(message?.detail?.key) }}
            </p>
            <div class="pull-progress-bar">
              <div
                class="pull-progress-bar__fill"
                :style="{ width: `${getProgressPercent(message?.detail?.key)}%` }"
              />
            </div>
          </div>
          <Button
            text
            icon="pi pi-times"
            class="p-0"
            style="color: hsl(var(--sidebar-foreground))"
            @click="closeCallback"
          />
        </div>
      </section>

      <!-- Default success / error rendering -->
      <div
        v-else
        class="p-toast-message-content"
      >
        <i
          class="p-toast-message-icon pi"
          :class="{
            'pi-check-circle': message.severity === 'success',
            'pi-times-circle': message.severity === 'error',
            'pi-info-circle': message.severity === 'info',
            'pi-exclamation-triangle': message.severity === 'warn',
          }"
        />
        <div class="p-toast-message-text">
          <span class="p-toast-summary">{{ message.summary }}</span>
        </div>
        <Button
          text
          icon="pi pi-times"
          class="p-toast-close-button p-0"
          @click="closeCallback"
        />
      </div>
    </template>
  </Toast>
</template>
<style scoped>
.toast-pull-container {
  background-color: hsl(var(--sidebar-background));
  color: hsl(var(--sidebar-foreground));
  border: 1px solid hsl(var(--sidebar-border));
}

.toast-pull-container p,
.toast-pull-container span,
.toast-pull-container i {
  color: hsl(var(--sidebar-foreground)) !important;
}

.pull-progress-bar {
  width: 100%;
  height: 0.35rem;
  border-radius: 999px;
  background-color: hsl(var(--sidebar-border));
  overflow: hidden;
}

.pull-progress-bar__fill {
  height: 100%;
  background-color: hsl(var(--sidebar-primary));
  transition: width 0.2s ease;
}
</style>
